import {
  Button,
  Grid,
  Switch as ToggleSwitch,
  useInterstateToken
} from "@dealer-center/core-components";
import styled from "styled-components";
import { SaveModal } from "./save";
import type { resourceRefsInterface } from ".";
import type { resources } from "@dealer-center/ddc-pages-resources-interface";
import { CompareTool } from "./compare";
import { useWindowDimensions } from "@dealer-center/core-custom-components";
import { useNavigate } from "react-router-dom";
interface HeaderAreaInterface {
  handleShowOnlyConfigurableToggled: () => void;
  handleShowOnlySelectedToggled: () => void;
  currentResources: resources[];
  resetClicked: () => void;
  resourceRefs: resourceRefsInterface;
  firstLetters: string[];
  existingResources: resources[];
  showOnlyConfigurableToggle: boolean;
  showOnlyCheckedToggle: boolean;
  dealerId: string;
}
const StyledLetterLinkArea = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding-left: 0;
  list-style: none;
  margin-left: -3px;
  clip-path: inset(0 0 0 3px);
  margin-top: 0;
  margin-bottom: 0;
`;
const StyledLetterLink = styled.li`
  padding-left: 1px;
  &::before {
    content: "";
    margin-right: 1px;
    border-left: solid 1px #6d6d6d;
  }
`;
const StyledLetterLinkButton = styled(Button)``;
const showAllLetters = false;
const StyledHeaderArea = styled.div<{ borderColor: string }>`
  margin-bottom: 1em;
  padding-bottom: 1em;
  border-bottom: 1px solid ${props => props.borderColor};

  /* Switch labels render black. Nested under the wrapper class so this outranks
     the single emotion class MUI puts on the label element, and scoped to
     MuiFormLabel so the button and letter-link colours are left alone.
     Interstate's Switch exposes no label-colour prop, so this reaches for an
     MUI-internal class name: stable within a MUI major, but a major upgrade can
     rename it and silently drop the colour with no type error to catch it. The
     component test asserts the computed colour so that regression fails loudly
     instead. */
  .MuiFormLabel-root {
    color: #000;
  }
`;

export function HeaderArea({
  handleShowOnlyConfigurableToggled,
  handleShowOnlySelectedToggled,
  currentResources,
  resetClicked,
  firstLetters,
  resourceRefs,
  existingResources,
  showOnlyConfigurableToggle,
  showOnlyCheckedToggle,
  dealerId
}: HeaderAreaInterface) {
  const navigate = useNavigate();
  const { isSmOrGreater } = useWindowDimensions();
  const getToken = useInterstateToken();
  const borderColor = getToken("sem.color.border.default") || "#6d6d6d";

  const handleAuditHistoryButton = () => {
    navigate(`/dealer/${dealerId}/resourceAutoLog`);
  };

  return (
    <StyledHeaderArea borderColor={borderColor}>
      <Grid container alignItems="center" spacing={isSmOrGreater ? 2 : 1}>
        <Grid>
          <StyledLetterLinkArea>
            {showAllLetters
              ? "abcdefghijklmnopqrstuvwxyz"
              : firstLetters
                  .join("")
                  .toUpperCase()
                  .split("")
                  .map(key => (
                    <StyledLetterLink key={key}>
                      <StyledLetterLinkButton
                        analyticsConfiguration={{
                          eventName: "Letter Link Clicked"
                        }}
                        buttonStyle="aux"
                        disabled={!firstLetters.includes(key)}
                        sx={{
                          width: "1.25em",
                          fontSize: "1em",
                          padding: "0 0.25em",
                          fontWeight: "600"
                        }}
                        onClick={() => {
                          const resource = resourceRefs[key];
                          if (!resource?.ref.current) return;
                          resource.ref.current.scrollIntoView({
                            behavior: "smooth"
                          });
                        }}
                      >
                        {key}
                      </StyledLetterLinkButton>
                    </StyledLetterLink>
                  ))}
          </StyledLetterLinkArea>
        </Grid>
        {/* Button lockup — Interstate orders a lockup by ascending emphasis
            left-to-right, so the primary action sits furthest right: tertiary
            (Audit), secondary (Compare, and Reset once there are changes),
            primary (Save). */}
        <Grid
          container
          alignItems="center"
          marginLeft="auto"
          spacing={isSmOrGreater ? 2 : 1}
          width="auto"
        >
          <Grid>
            <ToggleSwitch
              checked={showOnlyConfigurableToggle}
              label="Show only configurable"
              layout="vertical"
              onClick={handleShowOnlyConfigurableToggled}
            />
          </Grid>
          <Grid>
            <ToggleSwitch
              checked={showOnlyCheckedToggle}
              label="Show only selected"
              layout="vertical"
              onClick={handleShowOnlySelectedToggled}
            />
          </Grid>
          <Grid>
            <Button
              analyticsConfiguration={{
                eventName: "Account resources - Go to Audit History Page"
              }}
              buttonStyle="tertiary"
              onClick={handleAuditHistoryButton}
            >
              Audit
            </Button>
          </Grid>
          <CompareTool />
          <Grid>
            <SaveModal
              currentResources={currentResources}
              existingResources={existingResources}
              resetClicked={resetClicked}
            />
          </Grid>
        </Grid>
      </Grid>
    </StyledHeaderArea>
  );
}

export default HeaderArea;
